import React from 'react';
import {
    createSourcesFolderInParent,
    fetchSourcesFoldersByParent,
    removeSourcesFile,
    removeSourcesFolder,
    SourceDeletionChoiceRequiredError,
} from './sourcesApi';
import {clearSourcesCache} from './sourcesCacheAdapter';
import {removeItemsFromSources} from './sourcesListMutations';

const ROOT_MOVE_TARGET = '__ROOT__';

const useSourcesBulkActions = ({
    activeView,
    currentFolderId,
    displayedSources,
    sources,
    isFolderSelectable,
    selectedEntries,
    selectedCount,
    clearSelection,
    userId,
    moveEntryToFolder,
    reloadActiveViewSilently,
    setSources,
}) => {
    const [moveDialogOpen, setMoveDialogOpen] = React.useState(false);
    const [moveTargetFolderId, setMoveTargetFolderId] = React.useState(ROOT_MOVE_TARGET);
    const [isBulkMoving, setIsBulkMoving] = React.useState(false);
    const [bulkMoveError, setBulkMoveError] = React.useState(null);
    const [allUserFolders, setAllUserFolders] = React.useState([]);
    // When non-null, the move dialog operates on just this one entry (single-file / single-folder move).
    const [singleMoveEntry, setSingleMoveEntry] = React.useState(null);

    const [newFolderDialogOpen, setNewFolderDialogOpen] = React.useState(false);
    const [newFolderName, setNewFolderName] = React.useState('');
    const [isCreatingSelectionFolder, setIsCreatingSelectionFolder] = React.useState(false);
    const [newFolderError, setNewFolderError] = React.useState(null);

    const [deleteSelectionDialogOpen, setDeleteSelectionDialogOpen] = React.useState(false);
    const [isDeletingSelection, setIsDeletingSelection] = React.useState(false);
    const [deleteSelectionError, setDeleteSelectionError] = React.useState(null);

    const applyLocalSelectionRemoval = React.useCallback((entries) => {
        const fileIds = entries.filter((entry) => entry.itemType === 'file').map((entry) => entry.id);
        const folderIds = entries.filter((entry) => entry.itemType === 'folder').map((entry) => entry.id);
        setSources(removeItemsFromSources(sources, {fileIds, folderIds}));
        clearSourcesCache(activeView).catch(() => {});
    }, [activeView, setSources, sources]);

    const moveTargets = React.useMemo(() => {
        const rootLabel = activeView.type === 'folder' ? 'Root (My files)' : 'My files root';

        const targets = [
            {id: ROOT_MOVE_TARGET, label: rootLabel},
        ];

        // Add all user folders fetched when the dialog was opened (full folder tree root).
        // Fall back to currently-visible folders if the fetch hasn't completed yet.
        const folderSource = allUserFolders.length > 0
            ? allUserFolders
            : (displayedSources.folders || []).filter((f) => isFolderSelectable(f)).map((f) => ({
                id: String(f.id),
                name: f.name || 'Untitled folder',
            }));

        folderSource.forEach((folder) => {
            // Exclude the current folder itself (can't move into itself).
            if (currentFolderId && String(folder.id) === String(currentFolderId)) {
                return;
            }
            targets.push({id: String(folder.id), label: folder.name || 'Untitled folder'});
        });

        return targets;
    }, [activeView.type, allUserFolders, currentFolderId, displayedSources.folders, isFolderSelectable]);

    const resolveMoveTarget = React.useCallback(() => {
        if (moveTargetFolderId === ROOT_MOVE_TARGET) {
            return null;
        }
        return moveTargetFolderId || null;
    }, [moveTargetFolderId]);

    const handleMoveSelection = React.useCallback(async () => {
        const entriesToMove = singleMoveEntry ? [singleMoveEntry] : selectedEntries;
        if (!userId || entriesToMove.length === 0 || isBulkMoving) {
            return;
        }

        const targetFolderId = resolveMoveTarget();
        setIsBulkMoving(true);
        setBulkMoveError(null);
        try {
            await Promise.all(entriesToMove.map((entry) => moveEntryToFolder(entry, targetFolderId)));

            await reloadActiveViewSilently();
            if (!singleMoveEntry) {
                clearSelection();
            }
            setSingleMoveEntry(null);
            setMoveDialogOpen(false);
        } catch (moveError) {
            console.error('[SourcesSection] Failed to move selected items:', moveError);
            setBulkMoveError(moveError?.message || 'Failed to move selected items.');
        } finally {
            setIsBulkMoving(false);
        }
    }, [
        clearSelection,
        isBulkMoving,
        moveEntryToFolder,
        reloadActiveViewSilently,
        resolveMoveTarget,
        selectedEntries,
        singleMoveEntry,
        userId,
    ]);

    const handleCreateFolderWithSelection = React.useCallback(async () => {
        if (!userId || selectedCount === 0 || isCreatingSelectionFolder) {
            return;
        }

        const trimmedName = newFolderName.trim();
        if (!trimmedName) {
            setNewFolderError('Folder name is required.');
            return;
        }

        setIsCreatingSelectionFolder(true);
        setNewFolderError(null);
        try {
            const createdFolder = await createSourcesFolderInParent({
                userId,
                name: trimmedName,
                parentFolderId: currentFolderId ?? null,
            });

            await Promise.all(selectedEntries.map((entry) => moveEntryToFolder(entry, createdFolder.id)));

            await reloadActiveViewSilently();
            clearSelection();
            setNewFolderDialogOpen(false);
            setNewFolderName('');
        } catch (folderError) {
            console.error('[SourcesSection] Failed to create folder with selected items:', folderError);
            setNewFolderError(folderError?.message || 'Failed to create folder with selected items.');
        } finally {
            setIsCreatingSelectionFolder(false);
        }
    }, [
        clearSelection,
        currentFolderId,
        isCreatingSelectionFolder,
        moveEntryToFolder,
        newFolderName,
        reloadActiveViewSilently,
        selectedCount,
        selectedEntries,
        userId,
    ]);

    const handleDeleteSelection = React.useCallback(async () => {
        if (!userId || selectedCount === 0 || isDeletingSelection) {
            return;
        }

        setIsDeletingSelection(true);
        setDeleteSelectionError(null);
        try {
            for (const entry of selectedEntries) {
                try {
                    if (entry.itemType === 'folder') {
                        await removeSourcesFolder({folderId: entry.id, userId});
                    } else {
                        await removeSourcesFile({fileId: entry.id, userId});
                    }
                } catch (deleteError) {
                    if (deleteError instanceof SourceDeletionChoiceRequiredError) {
                        throw new Error(
                            `"${entry.name}" exists in multiple locations. Delete items individually to choose whether to remove a placement or delete everywhere.`,
                        );
                    }
                    throw deleteError;
                }
            }

            applyLocalSelectionRemoval(selectedEntries);
            clearSelection();
            setDeleteSelectionDialogOpen(false);
            reloadActiveViewSilently().catch(() => {});
        } catch (deleteError) {
            console.error('[SourcesSection] Failed to delete selected items:', deleteError);
            setDeleteSelectionError(deleteError?.message || 'Failed to delete selected items.');
        } finally {
            setIsDeletingSelection(false);
        }
    }, [
        applyLocalSelectionRemoval,
        clearSelection,
        isDeletingSelection,
        reloadActiveViewSilently,
        selectedCount,
        selectedEntries,
        userId,
    ]);

    const handleBulkMoveRequest = React.useCallback(() => {
        setBulkMoveError(null);
        setMoveTargetFolderId(ROOT_MOVE_TARGET);
        setAllUserFolders([]);
        setSingleMoveEntry(null);
        setMoveDialogOpen(true);
        // Eagerly fetch all root-level user folders so the picker shows the full tree.
        if (userId) {
            fetchSourcesFoldersByParent({userId, parentFolderId: null})
                .then((folders) => setAllUserFolders(folders))
                .catch(() => { /* non-fatal: fall back to visible folders */ });
        }
    }, [userId]);

    const handleSingleFileMoveRequest = React.useCallback((file) => {
        if (!file) {
            return;
        }
        setBulkMoveError(null);
        setMoveTargetFolderId(ROOT_MOVE_TARGET);
        setAllUserFolders([]);
        setSingleMoveEntry({id: String(file.id), itemType: 'file', name: file.name || ''});
        setMoveDialogOpen(true);
        if (userId) {
            fetchSourcesFoldersByParent({userId, parentFolderId: null})
                .then((folders) => setAllUserFolders(folders))
                .catch(() => { /* non-fatal: fall back to visible folders */ });
        }
    }, [userId]);

    const handleBulkNewFolderRequest = React.useCallback(() => {
        setNewFolderError(null);
        setNewFolderName('');
        setNewFolderDialogOpen(true);
    }, []);

    const handleBulkDeleteRequest = React.useCallback(() => {
        setDeleteSelectionError(null);
        setDeleteSelectionDialogOpen(true);
    }, []);

    const handleMoveDialogClose = React.useCallback(() => {
        if (!isBulkMoving) {
            setMoveDialogOpen(false);
            setSingleMoveEntry(null);
        }
    }, [isBulkMoving]);

    const handleMoveTargetChange = React.useCallback((event) => {
        setMoveTargetFolderId(event.target.value);
    }, []);

    const handleNewFolderDialogClose = React.useCallback(() => {
        if (!isCreatingSelectionFolder) {
            setNewFolderDialogOpen(false);
        }
    }, [isCreatingSelectionFolder]);

    const handleNewFolderNameChange = React.useCallback((event) => {
        setNewFolderName(event.target.value);
        if (newFolderError) {
            setNewFolderError(null);
        }
    }, [newFolderError]);

    const handleDeleteSelectionDialogClose = React.useCallback(() => {
        if (!isDeletingSelection) {
            setDeleteSelectionDialogOpen(false);
        }
    }, [isDeletingSelection]);

    return {
        moveDialogOpen,
        moveTargetFolderId,
        isBulkMoving,
        bulkMoveError,
        moveTargets,
        singleMoveEntry,
        newFolderDialogOpen,
        newFolderName,
        isCreatingSelectionFolder,
        newFolderError,
        deleteSelectionDialogOpen,
        isDeletingSelection,
        deleteSelectionError,
        handleMoveSelection,
        handleCreateFolderWithSelection,
        handleDeleteSelection,
        handleBulkMoveRequest,
        handleSingleFileMoveRequest,
        handleBulkNewFolderRequest,
        handleBulkDeleteRequest,
        handleMoveDialogClose,
        handleMoveTargetChange,
        handleNewFolderDialogClose,
        handleNewFolderNameChange,
        handleDeleteSelectionDialogClose,
    };
};

export default useSourcesBulkActions;
